/* ==========================================
   ⚠️ CUSTOM ASSET: Shared Upsell Add-to-Cart Helper
   Last Modified: 2026-10-03
   Author: Chad Walker (via Claude Code)
   ==========================================

   FIX 2026-07-29 (stale-bar-native-mutation-brief.md), SUPERSEDED SAME
   DAY: Dawn's OWN native cart-page quantity stepper and line-remove
   button never call WF.addToCart/removeFromCart at all — they go
   through Dawn's own CartItems class (assets/cart.js, unmodified),
   which requests only its own stock section list and has never heard
   of the placeable bar section, so the 2026-07-28 sequencing fix never
   applied to that route. Reproduced live: bumping the wreath's quantity
   via the native stepper refreshed the items list correctly but left
   the bar showing the previous cart's numbers, with no console.warn at
   all — proof the bar's section id was never even in that request, not
   that the response omitted it. First fix here was a pub/sub relay
   (subscribe to Dawn's hardcoded source: 'cart-items' event, fire an
   independent follow-up fetch for the bar section). That worked, but
   Chad's own standing rule is "if you need to directly edit the file,
   fork it and point to our wf- file instead" rather than work around it
   from outside — so it was replaced same day with
   assets/wf-cart-items-patch.js, which extends
   CartItems.getSectionsToRender() (and CartDrawerItems' override of the
   same method) from the outside, after Dawn's own classes register,
   so the bar section rides along on Dawn's own single native request
   instead of a second independent one chasing it afterward. This file
   no longer does anything for the native-mutation route at all — see
   that patch file's header comment for how it works. Honest gap: the
   patch does NOT share cartRequestSeq, since it never calls into this
   file at all — it just adds an entry to Dawn's own list and lets
   Dawn's own unmodified fetch/apply logic run. A native mutation
   racing an add/remove that goes through WF.addToCart at the exact
   same moment could still apply out of order between the two
   mechanisms. This is the same class of race Dawn's own stock behavior
   has always had between two simultaneous native actions (e.g. two
   quantity fields changed in quick succession) — the bar now simply
   inherits that pre-existing characteristic rather than being uniquely
   exposed to it, so it wasn't treated as a new problem to solve here.

   CHANGE 2026-07-28 (Option B, bar-option-b-placeable-relay.md part 2):
   the shipping progress bar moved out of #main-cart-footer into its own
   placeable section (sections/wf-shipping-progress-bar.liquid), addable/
   duplicatable/movable anywhere in the cart template from the theme
   editor. Its refresh registration can no longer assume a fixed id or a
   fixed count of instances, so cartSectionTargets() now also discovers
   every [data-wf-ship-bar-section] element actually in the DOM at
   request time and refreshes each one independently (zero instances:
   nothing to do; two instances placed side by side to compare
   placements: both refresh). shouldOpenDrawerFor() also had to learn
   about this new wrapper — the offer card's Add button used to be
   implicitly covered by the #main-cart-footer check when the bar lived
   there; now it needs its own check or it would start popping the
   drawer open when clicked from the cart page.

   FIX 2026-07-28 (same day, first end-to-end live test, Finding A in
   live-test-findings-brief.md): Chad reported the offer card's own Add
   button not refreshing the bar (stale gap text) while a hard page
   reload showed correct fresh numbers. Extensive direct testing against
   the live Ajax Cart API (driving /cart/add.js by hand with the exact
   same scenario and the exact same bundled sections list this file
   requests) proved Shopify's side of the round trip is 100% correct —
   the server always returns fresh, correct HTML for this section, every
   time, including bundled with cart-drawer/cart-icon-bubble/items/
   footer the way production actually requests it. That rules out a
   platform issue and points at a client-side race: if two cart
   mutations overlap (a quantity edit still in flight when an offer-card
   add is clicked, say), fetch responses can resolve out of order, and
   without a guard, whichever one resolves LAST wins regardless of which
   action was actually more recent. Added cartRequestSeq — every
   WF.addToCart/removeFromCart call gets a sequence number at request
   time, and notifyCartUpdate only touches the DOM if no newer request
   has been issued since. Also added a console.warn when a requested
   section id isn't in the response (verified this shouldn't happen —
   if it ever fires, that assumption broke) and wrapped each target's
   swap in try/catch so one failing target can't block the rest.

   Purpose:
     Single AJAX add/remove path for every shipping-upsell + add-on
     surface (cart page offer bar, bundle block, PDP add-on). Every
     surface calls WF.addToCart / WF.removeFromCart instead of writing its
     own fetch, so there is one cart-refresh path and one tracking call.

   Behavior:
     - Posts to the standard Shopify Ajax Cart API (routes.cart_add_url /
       routes.cart_change_url), requesting section re-renders the same way
       Dawn's own cart.js does.
     - Refreshes every cart surface actually present on the current page —
       the drawer's contents (#CartDrawer .drawer__inner; the drawer shell
       and overlay are never replaced, WF-DEC-0007), the header cart
       bubble (#cart-icon-bubble),
       and, only when they exist in the DOM, the cart page's own items and
       footer (#main-cart-items / #main-cart-footer) — directly from the
       one Ajax response, no second fetch. Real section IDs for the cart
       page are resolved from each element's own data-id attribute at
       request time, the same way Dawn's own CartItems.updateQuantity()
       does it in assets/cart.js. Dawn's own CartItems.onCartUpdate()
       instead hardcodes the literal "main-cart-items", which Shopify
       resolves BY FILENAME (sections/main-cart-items.liquid), not to this
       theme's cart.json section. It does not fail: it still runs after
       every cartUpdate this file publishes, and since Slice C1 that file
       delegates to the same snippet, so it returns identical markup
       (WF-DEC-0006). Corrected 2026-10-03 (comment only).
     - Also refreshes every placed instance of the placeable shipping
       progress bar section (any number of [data-wf-ship-bar-section]
       elements — zero, one, or several if Chad is comparing placements),
       discovered dynamically rather than assumed, since that section can
       be added/removed/duplicated/moved from the editor at any time.
     - Never forces the drawer open by itself — that's a separate, explicit
       decision (WF.openDrawer()) made by the caller/click-handler based on
       context. An earlier version always called
       CartDrawer.renderContents(), which unconditionally opens the drawer
       — fine for a PDP add (matches product-form.js's own convention) but
       wrong for a remove clicked from the cart page itself: it popped the
       drawer open AND never touched the cart page's own sections at all,
       so closing the drawer left stale content behind.
     - Optionally swaps a caller-specified section fragment so Liquid-
       driven state re-renders from the server instead of being computed
       in JS.
     - Fires a Klaviyo "Added to Cart" / "Removed from Cart" event with a
       Source property when window._learnq exists. There is no Klaviyo app
       on this store (onsite JS only — see layout/theme.liquid), so this is
       the only channel where an AJAX add wouldn't otherwise be tracked.
       GA4/Meta are expected to pick up cart mutations automatically via
       their apps' Web Pixels subscriptions, since this helper always goes
       through the standard Ajax Cart API — no separate dataLayer push here.
     - PDP add-on nested-line attach: subscribes to Dawn's existing
       PUB_SUB_EVENTS.cartUpdate (fired by product-form.js after every
       successful add). If a [data-wf-addon-checkbox] is checked and its
       enclosing <product-info>'s current variant id input matches the
       event's productVariantId, immediately follows up with a second
       /cart/add.js call using parent_line_key (the just-added line's key
       from the first response) — this is how a checked add-on rides
       along with the main Add to Cart click without touching
       product-form.js's own submit handler. Matching on the LIVE variant
       input value (not a Liquid-rendered one) so a variant switch after
       page load doesn't attach to the wrong line; matching productVariantId
       at all also means an unrelated product-form add elsewhere on the
       same page (e.g. a quick-add recommendation card) is correctly
       ignored.
     - Accelerated checkout (Shop Pay/Apple Pay) does not fire
       product-form.js's submit handler or this event, so the nested-line
       attach above never runs for it. Instead uses Shopify's documented
       multiple-product checkout convention: hidden
       shopify-additional-items[1][id] / [1][parent_id] inputs, form-
       associated via the form attribute (see
       https://shopify.dev/docs/storefronts/themes/pricing-payments/accelerated-checkout/multiple-product-checkout).
       Those inputs live in wf-pdp-addon.liquid; this file only keeps them
       enabled/disabled in step with the checkbox (disabled = excluded
       from submission entirely) and keeps parent_id synced to the live
       selected variant, since a variant switch after checking the box
       must not submit a stale parent.

   Usage:
     WF.addToCart(variantId, quantity, source)
     WF.addToCart(variantId, quantity, source, { sectionId, sectionSelector })
     WF.removeFromCart(variantId, source)
     WF.removeFromCart(variantId, source, { sectionId, sectionSelector })
     WF.openDrawer() — opens the cart drawer if one exists on the page.
       Neither addToCart/removeFromCart call this internally; the
       document-level click delegation below decides whether to call it,
       based on where the triggering element lives (see
       shouldOpenDrawerFor). A caller wiring up its own button outside
       that delegation (there shouldn't be one — see the markup helper
       below) would need to call it explicitly.

     source is a short string identifying the surface, e.g. 'pdp_addon',
     'cart_page_offer', 'bundle_block'. sectionId/sectionSelector are only
     needed when the caller has its own Liquid state to refresh beyond
     what notifyCartUpdate already covers (drawer, cart-icon-bubble, and
     the cart page's items/footer when present) — no current caller needs
     this, it remains available for a future one that does.

     Markup helper — every upsell surface should use these data attributes
     instead of wiring its own click listener. Delegation lives here, not
     per-snippet, because the cart drawer (and any section swapped via the
     Section Rendering API) gets replaced with fresh HTML on every cart
     mutation via .innerHTML — inline <script> tags in that HTML never
     execute, and a per-snippet addEventListener would go dead after the
     first refresh. Binding once on document survives every swap.

       <button data-wf-add-variant="{{ variant.id }}" data-wf-source="pdp_widget">
       <button data-wf-remove-variant="{{ variant.id }}" data-wf-source="pdp_widget">

     Optional: data-wf-quantity (default 1), data-wf-section-id +
     data-wf-section-selector (only if this surface has its own Liquid
     state to refresh beyond the cart drawer).
   ========================================== */

window.WF = window.WF || {};

(function () {
  'use strict';

  // Real section IDs for the cart page are read from each element's own
  // data-id at request time — this theme's cart.json names those sections
  // "cart-items"/"cart-footer". Dawn's own CartItems.onCartUpdate() fetches
  // the literal "main-cart-items" instead, which resolves by filename to
  // sections/main-cart-items.liquid. That refresh still fires after every
  // cartUpdate published here and overwrites the cart body, so that file
  // must render the same snippet as wf-main-cart-items (WF-DEC-0006,
  // comment corrected 2026-10-03). Target lookups below each copy where
  // Dawn itself refreshes that surface in place: the drawer uses
  // CartDrawerItems' granularity, #CartDrawer .drawer__inner only, so
  // #CartDrawer-Overlay and its Dawn-bound click listener survive
  // (WF-DEC-0007; Slice C2, 2026-10-03 — previously the whole #CartDrawer
  // was replaced, which recreated the overlay without its listener);
  // #cart-icon-bubble is replaced directly; CartItems.updateQuantity()
  // replaces a nested .js-contents inside the page's items/footer
  // containers. Every entry is only included if its element actually
  // exists on the current page.
  // Shipping progress bar (2026-07-28, Option B part 2): placeable, so
  // there's no fixed id or fixed count to assume — Chad can add, remove,
  // duplicate, or move this section from the editor at any time. Discover
  // every instance actually in the DOM right now; zero instances placed
  // means callers of this get an empty list and do nothing. Factored out
  // on its own (2026-07-29, stale-bar-native-mutation-brief.md) because
  // it's now needed from two places: the normal request/response path
  // below, and the independent native-mutation refresh further down.
  function shipBarSectionTargets() {
    var targets = [];
    document.querySelectorAll('[data-wf-ship-bar-section]').forEach(function (el) {
      if (!el.dataset.id) return;
      targets.push({
        id: el.dataset.id,
        target: el.querySelector('.js-contents') || el,
        selector: '.js-contents'
      });
    });
    return targets;
  }

  function cartSectionTargets() {
    var targets = [];

    var drawer = document.querySelector('#CartDrawer .drawer__inner');
    if (drawer) targets.push({ id: 'cart-drawer', target: drawer, selector: '.drawer__inner' });

    var bubble = document.getElementById('cart-icon-bubble');
    if (bubble) targets.push({ id: 'cart-icon-bubble', target: bubble, selector: '.shopify-section' });

    var items = document.getElementById('main-cart-items');
    if (items && items.dataset.id) {
      targets.push({
        id: items.dataset.id,
        target: items.querySelector('.js-contents') || items,
        selector: '.js-contents'
      });
    }

    var footer = document.getElementById('main-cart-footer');
    if (footer && footer.dataset.id) {
      targets.push({
        id: footer.dataset.id,
        target: footer.querySelector('.js-contents') || footer,
        selector: '.js-contents'
      });
    }

    return targets.concat(shipBarSectionTargets());
  }

  function baseSections(extraSectionId) {
    var ids = [];
    cartSectionTargets().forEach(function (t) {
      if (ids.indexOf(t.id) === -1) ids.push(t.id);
    });
    if (extraSectionId && ids.indexOf(extraSectionId) === -1) ids.push(extraSectionId);
    return ids;
  }

  function getSectionInnerHTML(html, selector) {
    var parsed = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return parsed ? parsed.innerHTML : null;
  }

  function swapSection(sectionId, sectionSelector, response) {
    if (!sectionId || !sectionSelector) return;
    if (!response.sections || !response.sections[sectionId]) return;
    var target = document.querySelector(sectionSelector);
    if (!target) return;
    var html = getSectionInnerHTML(response.sections[sectionId], sectionSelector);
    if (html !== null) target.innerHTML = html;
  }

  // Request sequencing (2026-07-28, live-test-findings-brief.md Finding A):
  // if two cart mutations overlap (e.g. a quantity edit still in flight when
  // an offer-card add is clicked), fetch responses can resolve out of order.
  // Without this guard, whichever response resolves LAST wins regardless of
  // which action was actually more recent, which can leave a stale render on
  // screen after a newer action already completed. Every call gets a
  // sequence number at request time; a response only touches the DOM if no
  // newer request has been issued since.
  var cartRequestSeq = 0;

  // Returns the ids of the targets actually refreshed.
  function applySectionsToTargets(targets, sectionsDict) {
    var applied = [];
    targets.forEach(function (t) {
      if (!sectionsDict[t.id]) {
        // Verified via direct /cart/add.js testing (2026-07-28) that Shopify
        // does include this theme's sections in the response when asked --
        // if this ever fires, that assumption broke; it's the first place
        // to look.
        console.warn('WF cart refresh: no section HTML returned for', t.id);
        return;
      }
      try {
        var html = getSectionInnerHTML(sectionsDict[t.id], t.selector);
        if (html !== null) {
          t.target.innerHTML = html;
          applied.push(t.id);
        }
      } catch (e) {
        // One target failing to parse/apply must never block the rest --
        // this loop covers independent surfaces (drawer, bubble, items,
        // footer, any number of placed progress-bar instances).
        console.error('WF cart refresh failed for', t.id, e);
      }
    });
    return applied;
  }

  // <cart-drawer>.is-empty is a caller duty in Dawn (cart.js toggles it from
  // item_count, product-form.js removes it after an add); it drives the
  // drawer's header visibility and focus-trap target. Only synced when the
  // drawer markup was actually refreshed, so class and markup never
  // disagree (WF-DEC-0007).
  function syncDrawerEmptyState(response) {
    var cartDrawer = document.querySelector('cart-drawer');
    if (!cartDrawer) return;
    if (typeof response.item_count === 'number') {
      cartDrawer.classList.toggle('is-empty', response.item_count === 0);
    } else {
      // /cart/add.js responses carry no item_count; a successful add means
      // the cart is non-empty.
      cartDrawer.classList.remove('is-empty');
    }
  }

  function notifyCartUpdate(response, source, requestId) {
    if (requestId !== undefined && requestId !== cartRequestSeq) return;
    if (!response.sections) return;

    var applied = applySectionsToTargets(cartSectionTargets(), response.sections);
    if (applied.indexOf('cart-drawer') !== -1) syncDrawerEmptyState(response);

    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      // Courtesy broadcast for anything else listening (analytics, future
      // features) — the actual DOM refresh above never depends on this.
      publish(PUB_SUB_EVENTS.cartUpdate, { source: source || 'wf-upsell', cartData: response });
    }
  }

  // Native quantity-stepper/line-remove mutations (Dawn's own CartItems/
  // CartDrawerItems) are handled by assets/wf-cart-items-patch.js instead
  // of a pub/sub relay here — see that file's header comment. Retired
  // 2026-07-29: this file previously subscribed to
  // PUB_SUB_EVENTS.cartUpdate for Dawn's hardcoded source: 'cart-items'
  // and fired an independent follow-up fetch for the bar section(s) after
  // every native mutation. That worked, but cost a second network
  // round-trip on every native quantity change/remove that patching
  // Dawn's own getSectionsToRender() (so the bar rides along on Dawn's
  // own single request) doesn't need.

  WF.openDrawer = function () {
    var drawer = document.querySelector('cart-drawer');
    if (drawer && typeof drawer.open === 'function') drawer.open();
  };

  function trackKlaviyo(eventName, variantId, source) {
    if (typeof window._learnq === 'undefined') return;
    window._learnq.push([
      'track',
      eventName,
      {
        Source: source,
        VariantId: variantId
      }
    ]);
  }

  function request(url, body) {
    return fetch(url, Object.assign({}, fetchConfig(), { body: JSON.stringify(body) }))
      .then(function (r) {
        return r.json();
      })
      .then(function (data) {
        if (data.status) {
          throw new Error(data.description || data.message || 'Cart request failed');
        }
        return data;
      });
  }

  WF.addToCart = function (variantId, quantity, source, opts) {
    opts = opts || {};
    var requestId = ++cartRequestSeq;
    var body = {
      items: [{ id: variantId, quantity: quantity || 1 }],
      sections: baseSections(opts.sectionId),
      sections_url: window.location.pathname
    };

    return request(window.routes.cart_add_url, body).then(function (response) {
      notifyCartUpdate(response, source, requestId);
      swapSection(opts.sectionId, opts.sectionSelector, response);
      trackKlaviyo('Added to Cart', variantId, source);
      return response;
    });
  };

  WF.removeFromCart = function (variantId, source, opts) {
    opts = opts || {};
    var requestId = ++cartRequestSeq;
    var body = {
      id: variantId,
      quantity: 0,
      sections: baseSections(opts.sectionId),
      sections_url: window.location.pathname
    };

    return request(window.routes.cart_change_url, body).then(function (response) {
      notifyCartUpdate(response, source, requestId);
      swapSection(opts.sectionId, opts.sectionSelector, response);
      trackKlaviyo('Removed from Cart', variantId, source);
      return response;
    });
  };

  // A remove never opens the drawer (matches Dawn's own CartRemoveButton,
  // which just refreshes in place) — the bug this replaced was a remove
  // clicked from the cart page popping the drawer open and never touching
  // the page underneath. An add opens the drawer as confirmation UNLESS
  // it was triggered from within the cart page itself (#main-cart-items /
  // #main-cart-footer, or the placeable shipping bar section's own offer
  // card — see data-wf-ship-bar-section below, added 2026-07-28 when the
  // bar moved out of #main-cart-footer into its own section and its offer
  // card's Add button would otherwise have started popping the drawer
  // open on the cart page), where popping a drawer over content the
  // customer is already looking at would be the same unwanted surprise.
  function shouldOpenDrawerFor(el) {
    return (
      !el.closest('#main-cart-items') &&
      !el.closest('#main-cart-footer') &&
      !el.closest('[data-wf-ship-bar-section]')
    );
  }

  document.addEventListener('click', function (evt) {
    var addBtn = evt.target.closest('[data-wf-add-variant]');
    if (addBtn) {
      if (addBtn.disabled) return;
      evt.preventDefault();
      addBtn.disabled = true;
      var openDrawerAfter = shouldOpenDrawerFor(addBtn);
      WF.addToCart(
        addBtn.dataset.wfAddVariant,
        parseInt(addBtn.dataset.wfQuantity || '1', 10),
        addBtn.dataset.wfSource || 'unknown',
        { sectionId: addBtn.dataset.wfSectionId, sectionSelector: addBtn.dataset.wfSectionSelector }
      )
        .then(function () {
          if (openDrawerAfter) WF.openDrawer();
        })
        .catch(function (e) {
          console.error(e);
        })
        .finally(function () {
          addBtn.disabled = false;
        });
      return;
    }

    var removeBtn = evt.target.closest('[data-wf-remove-variant]');
    if (removeBtn) {
      if (removeBtn.disabled) return;
      evt.preventDefault();
      removeBtn.disabled = true;
      WF.removeFromCart(
        removeBtn.dataset.wfRemoveVariant,
        removeBtn.dataset.wfSource || 'unknown',
        { sectionId: removeBtn.dataset.wfSectionId, sectionSelector: removeBtn.dataset.wfSectionSelector }
      )
        .catch(function (e) {
          console.error(e);
        })
        .finally(function () {
          removeBtn.disabled = false;
        });
    }
  });

  // --- PDP add-on / bundle: per-row quantity stepper ---
  //
  // Bundle rows carry a [data-wf-addon-qty] input, revealed only while the
  // row is checked. The single-add-on block (wf-pdp-addon.liquid) has none,
  // so its quantity stays 1.
  function addonQty(row) {
    var input = row && row.querySelector('[data-wf-addon-qty]');
    if (!input) return 1;
    var max = parseInt(input.max, 10) || 99;
    var qty = parseInt(input.value, 10);
    if (isNaN(qty) || qty < 1) qty = 1;
    if (qty > max) qty = max;
    return qty;
  }

  function setAddonQty(row, qty) {
    var input = row && row.querySelector('[data-wf-addon-qty]');
    if (!input) return;
    input.value = qty;
    qty = addonQty(row);
    input.value = qty;
    var max = parseInt(input.max, 10) || 99;
    row.querySelectorAll('[data-wf-addon-qty-step]').forEach(function (btn) {
      var dir = parseInt(btn.getAttribute('data-wf-addon-qty-step'), 10);
      btn.disabled = dir < 0 ? qty <= 1 : qty >= max;
    });
    var expressQty = row.querySelector('[data-wf-addon-express-qty]');
    if (expressQty) expressQty.value = qty;
  }

  document.addEventListener('click', function (evt) {
    var btn = evt.target.closest('[data-wf-addon-qty-step]');
    if (!btn) return;
    var row = btn.closest('[data-wf-addon-row]');
    setAddonQty(row, addonQty(row) + parseInt(btn.getAttribute('data-wf-addon-qty-step'), 10));
  });

  // --- PDP add-on: keep the accelerated-checkout hidden fields in step ---
  function syncAddonExpressFields(checkbox) {
    var wrapper = checkbox.closest('[data-wf-addon-row]') || checkbox.closest('.wf-pdp-addon');
    if (!wrapper) return;

    var qtyWrap = wrapper.querySelector('[data-wf-addon-qty-wrap]');
    if (qtyWrap) {
      qtyWrap.hidden = !checkbox.checked;
      setAddonQty(wrapper, addonQty(wrapper));
    }
    var expressQty = wrapper.querySelector('[data-wf-addon-express-qty]');
    if (expressQty) expressQty.disabled = !checkbox.checked;

    var idField = wrapper.querySelector('[data-wf-addon-express-id]');
    var parentIdField = wrapper.querySelector('[data-wf-addon-express-parent-id]');
    if (!idField || !parentIdField) return;

    idField.disabled = !checkbox.checked;
    parentIdField.disabled = !checkbox.checked;

    if (checkbox.checked) {
      var productInfo = checkbox.closest('product-info');
      var variantInput = productInfo && productInfo.querySelector('input[name="id"]');
      if (variantInput) parentIdField.value = variantInput.value;
    }
  }

  // Bundle variant dropdown: swap the variant that gets added, the price /
  // compare-at / SAVE display, and the quantity ceiling.
  function onAddonVariantChange(select) {
    var row = select.closest('[data-wf-addon-row]');
    var opt = select.options[select.selectedIndex];
    if (!row || !opt) return;

    var checkbox = row.querySelector('[data-wf-addon-checkbox]');
    if (checkbox) checkbox.dataset.wfAddonVariant = opt.value;
    var idField = row.querySelector('[data-wf-addon-express-id]');
    if (idField) idField.value = opt.value;

    var price = row.querySelector('[data-wf-addon-price]');
    var was = row.querySelector('[data-wf-addon-was]');
    var save = row.querySelector('[data-wf-addon-save]');
    if (price) price.textContent = opt.dataset.price || '';
    if (was) { was.textContent = opt.dataset.was || ''; was.hidden = !opt.dataset.was; }
    if (save) { save.textContent = opt.dataset.save || ''; save.hidden = !opt.dataset.save; }

    var qtyInput = row.querySelector('[data-wf-addon-qty]');
    if (qtyInput) qtyInput.max = opt.dataset.max || 99;
    setAddonQty(row, addonQty(row));
  }

  document.addEventListener('change', function (evt) {
    if (evt.target.matches && evt.target.matches('[data-wf-addon-variant-select]')) {
      onAddonVariantChange(evt.target);
      return;
    }

    if (evt.target.matches && evt.target.matches('[data-wf-addon-qty]')) {
      var qtyRow = evt.target.closest('[data-wf-addon-row]');
      setAddonQty(qtyRow, addonQty(qtyRow));
      return;
    }

    var checkbox = evt.target.closest('[data-wf-addon-checkbox]');
    if (checkbox) {
      syncAddonExpressFields(checkbox);
      return;
    }

    // A variant switch while an add-on is checked must refresh parent_id,
    // not just the initial check — re-sync every checked add-on on this page.
    if (evt.target.matches && evt.target.matches('input[name="id"]')) {
      document.querySelectorAll('[data-wf-addon-checkbox]:checked').forEach(syncAddonExpressFields);
    }
  });

  // --- PDP add-on / bundle: attach via native nested cart lines after the main add ---
  //
  // Dawn's product-form.js owns the parent add (error handling, sold-out
  // state, drawer opening, section rendering). We never touch it. This
  // subscribes to the cartUpdate it publishes and attaches every checked
  // add-on in ONE follow-up request, so a 2-item bundle costs two network
  // calls total, not three.
  //
  // Each child carries a unique _bundle_id property. Shopify derives a line
  // key from variant id + properties, so without it the same variant bundled
  // under two different parents can collide on key and a remove would target
  // the wrong line.
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, function (data) {
      if (!data || data.source !== 'product-form') return;

      var parentKey = data.cartData && data.cartData.key;
      if (!parentKey) return;

      // Only rows belonging to the product-info whose variant matches the add
      // that just happened — ignores quick-add carousels elsewhere on the page.
      // Relies on one canonical variant per product-info (sticky ATC submits
      // the main form): WF-DEC-0001.
      var checked = [];
      document.querySelectorAll('[data-wf-addon-checkbox]:checked').forEach(function (cb) {
        var productInfo = cb.closest('product-info');
        if (!productInfo) return;
        var variantInput = productInfo.querySelector('input[name="id"]');
        if (!variantInput || String(variantInput.value) !== String(data.productVariantId)) return;
        if (!cb.dataset.wfAddonVariant) return;
        checked.push(cb);
      });
      if (!checked.length) return;

      var bundleId = 'bndl_' + Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
      var bundleEl = checked[0].closest('[data-wf-bundle-parent-title]');
      var parentTitle = bundleEl ? bundleEl.getAttribute('data-wf-bundle-parent-title') : '';

      var items = checked.map(function (cb) {
        var props = { _bundle_id: bundleId };
        if (parentTitle) props['Bundled With'] = parentTitle;
        return {
          id: cb.dataset.wfAddonVariant,
          quantity: addonQty(cb.closest('[data-wf-addon-row]')),
          parent_line_key: parentKey,
          properties: props
        };
      });

      var source = checked[0].dataset.wfAddonSource || 'pdp_addon';
      checked.forEach(function (cb) { cb.disabled = true; });

      request(window.routes.cart_add_url, {
        items: items,
        sections: baseSections(),
        sections_url: window.location.pathname
      })
        .then(function (response) {
          // product-form.js already opened the drawer for the parent; this
          // follow-up only refreshes it with the attached lines.
          notifyCartUpdate(response, source);
          checked.forEach(function (cb) {
            trackKlaviyo('Added to Cart', cb.dataset.wfAddonVariant, cb.dataset.wfAddonSource || source);
            cb.checked = false;
            var row = cb.closest('[data-wf-addon-row]');
            if (row) setAddonQty(row, 1);
            syncAddonExpressFields(cb);
          });
        })
        .catch(function (e) {
          console.error('WF bundle attach failed', e);
        })
        .finally(function () {
          checked.forEach(function (cb) { cb.disabled = false; });
        });
    });
  }
})();
